import { useEffect, useState } from "react";
import { Menu, X, Activity, LogOut, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useChatbot } from "@/context/ChatbotContext";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "About", href: "#about" },
  { label: "How it works", href: "#how" },
  { label: "Features", href: "#features" },
  { label: "Mental", href: "#mental" },
  { label: "Demo", href: "#demo" },
  { label: "FAQ", href: "#faq" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [explore, setExplore] = useState(false);
  const { openChatbot } = useChatbot();
  const { user, openAuth, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? "glass-header py-3" : "py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5 group"
        >
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-[#0B3D91] to-cyan-500 shadow-[0_0_20px_rgba(0,229,255,0.35)]">
            <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
          </span>
          <span className="font-display text-lg font-semibold tracking-tight">
            SilentScan<span className="text-cyan-400">.AI</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => scrollTo(l.href)}
              className="px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors rounded-full hover:bg-white/5"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <div className="relative">
            <button
              data-testid="nav-explore"
              onClick={() => setExplore((e) => !e)}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors rounded-full hover:bg-white/5"
            >
              Explore
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${explore ? "rotate-180" : ""}`} />
            </button>
            {explore && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExplore(false)} />
                <div className="absolute right-0 top-full mt-2 z-20 w-56 rounded-2xl glass-card border border-white/10 bg-[#0A0A14]/95 backdrop-blur-xl p-2 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
                  {[
                    { to: "/dashboard", label: "Dashboard" },
                    { to: "/nutrition", label: "Nutrition" },
                    { to: "/library", label: "Knowledge Library" },
                    { to: "/simulator", label: "Future Simulator" },
                    { to: "/wearables", label: "Wearables" },
                    { to: "/community", label: "Community" },
                    { to: "/neurorehab", label: "NeuroRehab AI" },
                    { to: "/neurospeak", label: "NeuroSpeak AI" },
                    { to: "/neuromotor", label: "NeuroMotor AI" },
                    { to: "/impact", label: "The Project" },
                    { to: "/founder", label: "Founder" },
                  ].map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-explore-${l.to.slice(1)}`}
                      onClick={() => setExplore(false)}
                      className="block px-4 py-2.5 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
          <Link
            to="/health-ai"
            data-testid="nav-health-ai"
            className="px-4 py-2 text-sm text-white font-medium transition-colors rounded-full bg-gradient-to-r from-[#0B3D91]/60 to-cyan-500/30 border border-cyan-400/30 hover:border-cyan-300/60"
          >
            Health AI
          </Link>
          <Link
            to="/diabetes"
            data-testid="nav-diabetes"
            className="px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors rounded-full hover:bg-white/5"
          >
            Diabetes Centre
          </Link>
          <Link
            to="/tools"
            data-testid="nav-tools"
            className="px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors rounded-full hover:bg-white/5"
          >
            Free Wellness Tools
          </Link>
          {user ? (
            <div className="flex items-center gap-1.5">
              <Link
                to="/history"
                data-testid="nav-user-chip"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-400/30 text-xs text-cyan-200 hover:bg-cyan-500/20 transition-colors"
              >
                <span className="w-5 h-5 rounded-full bg-gradient-to-br from-[#0B3D91] to-cyan-500 flex items-center justify-center text-[10px] font-semibold text-white">
                  {(user.name || user.email || "?").charAt(0).toUpperCase()}
                </span>
                {(user.name || user.email).split(" ")[0]}
              </Link>
              <button
                data-testid="nav-logout"
                onClick={logout}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 text-zinc-400 hover:text-white hover:border-rose-400/40 transition-colors"
                aria-label="Sign out"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              data-testid="nav-signin"
              onClick={openAuth}
              className="px-4 py-2 text-sm text-cyan-300 hover:text-white transition-colors rounded-full hover:bg-white/5 border border-cyan-400/30"
            >
              Sign in
            </button>
          )}
          <button
            data-testid="nav-try-demo"
            onClick={openChatbot}
            className="btn-glow rounded-full px-5 py-2.5 text-sm font-medium text-white"
          >
            Try Demo
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-full btn-outline-glass"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden mx-6 mt-3 glass-card rounded-2xl p-4 flex flex-col gap-1 max-h-[calc(100dvh-6rem)] overflow-y-auto no-scrollbar"
        >
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => scrollTo(l.href)}
              className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/dashboard"
            data-testid="nav-dashboard-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Dashboard
          </Link>
          <Link
            to="/assistant"
            data-testid="nav-assistant-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Assistant
          </Link>
          <Link
            to="/nutrition"
            data-testid="nav-nutrition-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Nutrition
          </Link>
          <Link
            to="/library"
            data-testid="nav-library-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Library
          </Link>
          <Link
            to="/simulator"
            data-testid="nav-simulator-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Simulator
          </Link>
          <Link
            to="/medical-id"
            data-testid="nav-medid-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Medical ID
          </Link>
          <Link
            to="/hospitals"
            data-testid="nav-hospitals-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Nearby Care
          </Link>
          <Link
            to="/wearables"
            data-testid="nav-wearables-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Wearables
          </Link>
          <Link
            to="/sleep"
            data-testid="nav-sleep-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Sleep
          </Link>
          <Link
            to="/family"
            data-testid="nav-family-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Family
          </Link>
          <Link
            to="/community"
            data-testid="nav-community-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Community
          </Link>
          <Link
            to="/neurorehab"
            data-testid="nav-neurorehab-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            NeuroRehab
          </Link>
          <Link
            to="/neurospeak"
            data-testid="nav-neurospeak-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            NeuroSpeak
          </Link>
          <Link
            to="/neuromotor"
            data-testid="nav-neuromotor-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            NeuroMotor
          </Link>
          <Link
            to="/impact"
            data-testid="nav-impact-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            The Project
          </Link>
          <Link
            to="/founder"
            data-testid="nav-founder-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Founder
          </Link>
          <Link
            to="/health-ai"
            data-testid="nav-health-ai-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-white font-medium rounded-xl transition-colors bg-gradient-to-r from-[#0B3D91]/60 to-cyan-500/30 border border-cyan-400/30"
          >
            Health AI
          </Link>
          <Link
            to="/diabetes"
            data-testid="nav-diabetes-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Diabetes Centre
          </Link>
          <Link
            to="/tools"
            data-testid="nav-tools-mobile"
            onClick={() => setOpen(false)}
            className="text-left px-4 py-3 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
          >
            Free Wellness Tools
          </Link>
          {user ? (
            <div className="flex items-center justify-between px-4 py-3 mt-1 rounded-xl bg-cyan-500/[0.07] border border-cyan-400/20">
              <span
                data-testid="nav-user-chip-mobile"
                className="text-sm text-cyan-200 truncate"
              >
                {user.name || user.email}
              </span>
              <button
                data-testid="nav-logout-mobile"
                onClick={() => {
                  setOpen(false);
                  logout();
                }}
                className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </button>
            </div>
          ) : (
            <button
              data-testid="nav-signin-mobile"
              onClick={() => {
                setOpen(false);
                openAuth();
              }}
              className="text-left px-4 py-3 text-sm text-cyan-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors border border-cyan-400/20 mt-1"
            >
              Sign in — save your results
            </button>
          )}
          <button
            data-testid="nav-try-demo-mobile"
            onClick={() => {
              setOpen(false);
              openChatbot();
            }}
            className="btn-glow rounded-xl px-5 py-3 text-sm font-medium text-white mt-2"
          >
            Try Demo
          </button>
        </div>
      )}
    </header>
  );
}
